/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Container, Hide, SimpleGrid, Skeleton } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import Heading from 'components/atoms/heading';
import { SelectOption } from 'components/molecules/jobs/filters/asyncSelect';
import { Footer } from 'components/organisms/footer';
import { DesktopFilters } from 'components/organisms/jobs/filters/desktop';
import { MobileFilters } from 'components/organisms/jobs/filters/mobile';
import { ShowcaseJobsList } from 'components/organisms/jobs/showcase';
import LandingPageHeader from 'components/organisms/landingPageHeader';
import { headerLinks } from 'lib/constants/header';
import { JOBS } from 'lib/constants/paths';
import { salaryDefaults } from 'lib/constants/salary';
import {
  FindShowcaseJobsQuery,
  SearchJobFilter,
  useFindMaxMinJobsSalaryQuery,
  useFindShowcaseJobsLazyQuery,
  useGetCandidateProfileDataQuery,
  useSearchTechsLazyQuery,
} from 'lib/generated/graphql';
import useSession from 'lib/hooks/useSession';
import { setCandidate } from 'lib/repositories/candidate.repository';
import {
  JobFiltersType,
  useJobFilters,
} from 'lib/repositories/jobsShowcaseFilters';
import {
  SALARY_DEFAULT_MAX,
  SALARY_DEFAULT_MIN,
} from 'lib/repositories/searchCandidateFilters.repository';

// const SeniorityFilterMap = (seniority: SeniorityEnum[]) => {
//   return jobExperienceLevelOptions.filter((option) => seniority.includes(option.value));
// }
export const ShowcasePageTemplate = () => {
  const router = useRouter();
  const [defaultSalaryValues, setDefaultSalaryValues] = useState<Array<number>>(
    [salaryDefaults.min, salaryDefaults.max]
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [jobs, setJobs] =
    useState<FindShowcaseJobsQuery['findShowcaseJobs']['data']>();
  const [totalJobs, setTotalJobs] = useState<number>(0);
  const { currentToken } = useSession();

  const [getTechs] = useSearchTechsLazyQuery();
  const [findShowcaseJobs] = useFindShowcaseJobsLazyQuery();
  const { loading: minMaxSalaryLoading } = useFindMaxMinJobsSalaryQuery({
    onCompleted: (data) => {
      setDefaultSalaryValues([
        data.findMaxMinJobsSalary?.minSalary || salaryDefaults.min,
        data.findMaxMinJobsSalary?.maxSalary || salaryDefaults.max,
      ]);
    },
  });

  const { filters, setTechs } = useJobFilters();

  useGetCandidateProfileDataQuery({
    skip: !currentToken,
    onCompleted: (data) => {
      setCandidate(data?.getCandidateProfileData.candidate);
    },
  });

  const handleSearchJobs = async (updatedFilters?: JobFiltersType) => {
    setIsLoading(true);
    setTotalJobs(0);
    setJobs([]);

    const applyFilters = updatedFilters
      ? { ...updatedFilters }
      : { ...filters };

    const showcaseParams: SearchJobFilter = {
      companyLocation: applyFilters.companyLocation,
      order: applyFilters.order,
      remoteWork: applyFilters?.type?.includes('remote') || false,
      pagination: {
        page: (applyFilters?.page || 1) - 1,
        perPage: 10,
      },
    };

    if (!!applyFilters.minSalary && !!applyFilters.maxSalary)
      showcaseParams.salaryRange = {
        min: applyFilters.minSalary || SALARY_DEFAULT_MIN,
        max: applyFilters.maxSalary || SALARY_DEFAULT_MAX,
      };

    if (applyFilters.city)
      showcaseParams.cities = [applyFilters.city.value.toString()];
    if (applyFilters.workMode) showcaseParams.workMode = applyFilters.workMode;
    if (applyFilters.seniority)
      showcaseParams.seniority = applyFilters.seniority;
    if (applyFilters.techs)
      showcaseParams.technologies = applyFilters?.techs?.map((tech) =>
        tech.value.toString()
      );
    if (applyFilters.focuses)
      showcaseParams.focuses = applyFilters.focuses.map((focus) => focus.id);
    if (applyFilters.companyLocation)
      showcaseParams.companyLocation = applyFilters.companyLocation;

    const { data, loading } = await findShowcaseJobs({
      variables: { showcaseParams },
    });

    setJobs(data?.findShowcaseJobs?.data || []);
    setTotalJobs(data?.findShowcaseJobs?.total || 0);
    setIsLoading(loading);
  };

  /**
   * Get jobs on first page load if the url has a tech param
   */
  useEffect(() => {
    const techQuery = router.query.tech as string;
    if (!techQuery) {
      handleSearchJobs();
      return;
    }

    setIsLoading(true);
    const techFilter: SelectOption[] = [];
    getTechs({
      variables: { textSearch: techQuery },
    })
      .then((response) => {
        if (response.data && response.data?.searchTechs.length > 0) {
          techFilter.push({
            text: response.data.searchTechs[0].name,
            value: response.data.searchTechs[0].id,
          });
          setTechs(techFilter);
        }
      })
      .finally(() => {
        handleSearchJobs({
          ...filters,
          techs: techFilter,
        });
      });
  }, []);

  return (
    <>
      <LandingPageHeader
        headerLinks={headerLinks.filter((link) => link.path === JOBS)}
      />

      <Box bgColor='white.200'>
        <Container maxW='container.xxl' pb={8} pt={16} px={[2.5, 4]}>
          <Heading
            color='text.900'
            fontWeight={400}
            format='h2'
            my={[10, 20]}
            textAlign={['start', 'center']}
          >
            As melhores vagas em <b>empresas de tecnologia 🚀</b>
          </Heading>

          <SimpleGrid
            templateColumns={['100%', '100%', '60% 40%', '70% 30%']}
            gap={4}
          >
            <Hide above='md'>
              <MobileFilters
                showOrderFilter={jobs !== undefined && jobs?.length > 0}
                defaultSalaryValues={defaultSalaryValues}
                handleSearchJobs={handleSearchJobs}
              />
            </Hide>

            <ShowcaseJobsList
              isCandidateAuthenticated={!!currentToken}
              handleSearchJobs={handleSearchJobs}
              jobs={jobs || []}
              loading={isLoading}
              total={totalJobs}
            />
            <Hide below='md'>
              <Skeleton isLoaded={!minMaxSalaryLoading}>
                <DesktopFilters
                  defaultSalaryValues={defaultSalaryValues}
                  handleSearchJobs={handleSearchJobs}
                />
              </Skeleton>
            </Hide>
          </SimpleGrid>
        </Container>
      </Box>

      <Footer />
    </>
  );
};
